import React from 'react';
import styled from '@emotion/styled';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import {
  Form,
  Formik,
  Field,
  ErrorMessage,
  FormikHelpers,
  FormikProps,
} from 'formik';
import ReCAPTCHA from 'react-google-recaptcha';
import { Typography, useMediaQuery, useTheme } from '@material-ui/core';

import { forgotPassword } from 'actions/app.actions';
import { InputFieldWithLabel, PrimaryButton } from 'components';
import { appConfigSelector } from 'reducers/app.reducer';
import { ICustomTheme } from 'interfaces';
import { ROUTES } from '../../constants';
import {
  ButtonContainer,
  InputsContainer,
  LinkContainer,
} from './ResetPasswordForm.styles';
import { LinkWithUIGuidelines } from 'component-library/atoms/LinkWithUIGuidelines';

type InputFormTypes = {
  loading: boolean;
};
type InitialValuesTypes = {
  email: string;
  isCaptchaValid: boolean;
};
type FormikTypes = {
  field: FormikHelpers<InitialValuesTypes>;
  form: FormikProps<InitialValuesTypes>;
};

export const CaptchaContainer = styled.div<{
  theme: ICustomTheme;
}>(({ theme }) => ({
  textAlign: 'center',
  '& > div > div > div': {
    margin: '0 auto',
  },
}));

export const ErrorMessageContainer = styled.div<{
  theme: ICustomTheme;
}>(({ theme }) => ({
  minHeight: theme.spacing(3),
}));

const initialValues: InitialValuesTypes = {
  email: '',
  isCaptchaValid: false,
};

const ForgotPasswordForm = ({ loading }: InputFormTypes): JSX.Element => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const theme: ICustomTheme = useTheme();
  const isDownSm = useMediaQuery(theme.breakpoints.down('sm'));

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .trim()
      .email(t('core.invalid_email_address'))
      .required(t('core.required')),
    isCaptchaValid: Yup.bool()
      .oneOf([true], t('core.required_captcha'))
      .required(t('core.required_captcha')),
  });

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleSubmit = ({ isCaptchaValid, email }: InitialValuesTypes) => {
    // exclude isCaptchaValid as we don't want to send it to API
    dispatch(forgotPassword.request(email.trim()));
  };

  const { recaptchaPublicKey } = useSelector(appConfigSelector);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      <Form>
        <InputsContainer>
          <InputFieldWithLabel
            label={t('auth.global.email')}
            name="email"
            placeholder={t('auth.global.email_placeholder')}
            required
          />
        </InputsContainer>

        <CaptchaContainer>
          <Field name="isCaptchaValid">
            {({ field, form }: FormikTypes): JSX.Element => (
              // When sitekey is falsy the component crash, we have an empty page
              <ReCAPTCHA
                {...field}
                sitekey={recaptchaPublicKey || 'invalid key'}
                onChange={(value) =>
                  form.setValues({ ...form.values, isCaptchaValid: !!value })
                }
                size={isDownSm ? 'compact' : 'normal'}
              />
            )}
          </Field>
          <ErrorMessageContainer>
            <ErrorMessage
              name="isCaptchaValid"
              component={(props) => (
                <Typography {...props} color="error" variant="caption" />
              )}
            />
          </ErrorMessageContainer>
        </CaptchaContainer>

        <ButtonContainer>
          <PrimaryButton
            label={t('auth.reset_password.submit')}
            isDisabled={loading}
            isLoading={loading}
          />
        </ButtonContainer>

        <LinkContainer>
          <LinkWithUIGuidelines
            isInternalLink
            url={ROUTES.LOGIN}
            color={theme.colors.paletteFromBO.main}
            textLink={t('auth.reset_password.return')}
            fontWeight="bold"
            withUnderline
          />
        </LinkContainer>
      </Form>
    </Formik>
  );
};

export default ForgotPasswordForm;
